<template>
    <li
        :class="[
          movement.transported ? 'bg-success' : 'bg-black',
          movement.transported_to_suit ? 'bg-amethyst' : '',
          'floating-movement rounded-pill',
          !lastItem ? 'mb-2' : '',
          isDragging ? 'dragging' : '',
          isDragOverIndex ? 'drag-over-target' : '',
          isRecentlyUpdated ? 'highlight-flash' : '',
          ...classes
        ]"
        :data-movement-id="movement.id"
        :data-status="movement.status"
        :data-planned-position="movement.planned_position"
        :data-licences="licences"
        data-bs-toggle="tooltip"
        data-bs-placement="right"
        :title="tooltipText"
        ref="tooltipElement"
        :draggable="isDragDropEnabled"
        @dragstart="onDragStart"
        @drag="onDrag"
        @dragend="onDragEnd"
        @dragover.prevent="onDragOver"
        @dragenter.prevent="onDragEnter"
        @drop.prevent="onDropOnItem"
    >
        <div class="d-flex align-items-stretch rounded-pill overflow-hidden">
            <div class="drag-handle d-flex align-items-center justify-content-center">
                <i class="fa fa-grip-vertical"></i>
            </div>

            <div class="movement-content flex-grow-1 py-3 pe-3">
                <div class="d-flex justify-content-between align-items-center text-white">
                    <div class="d-flex gap-1">
                        <span class="mx-2">
                            {{ formatLocale( movement.collection_locale ) }}
                            <i>to</i>
                            {{ formatLocale( movement.delivery_locale ) }}
                        </span>
                        <span>
                            <TimeLabel :time="movement.collection_time" />
                        </span>
                        <span v-if="movement.collection_split" class="">
                            <span class="badge bg-warning">Split Collection</span>
                        </span>
                        <span v-if="movement.delivery_split" class="">
                            <span class="badge bg-warning">Split Delivery</span>
                        </span>
                    </div>
                    <div>
                        <button
                            type="button"
                            class="border-0 p-0 text-center rounded-circle modal-icon"
                            :class="movement.status"
                            style="width: 20px; height: 20px; font-size: 10px"
                            @click.stop="showModal"
                        >
                            <i :class="['fa', movement.read_by_driver ? 'fa-check' : 'fa-info']"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </li>

    <div ref="modal" class="modal text-unset" :id="compModalIdentifier" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 900px;">
            <div class="modal-content" ref="modalBody">
                <div class="modal-header">
                    <h5 class="modal-title text-center">
                        Movement {{ movement.position }} for {{ movement.job.reference }}
                    </h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-header block-content-heading">
                    <div class="text-center w-100">
                        <p class="mb-0">
                            {{ movement.collection_locale }} - {{ movement.delivery_locale }}
                        </p>
                        <Transported
                            v-model="movement"
                        />
                    </div>
                </div>
                <div class="modal-body bg-light">
                    <div class="row">
                        <div class="col-6">
                            <div class="row">
                                <div class="col-12">
                                    <AdminBlock title="Driver Notes">
                                        <p class="mb-0">
                                            {{ movement.driver_notes ?? 'No Driver Notes' }}
                                        </p>
                                        <template #options>
                                            <a class="btn btn-sm btn-secondary" :href="driverEditLink()" target="_blank">
                                                Edit on Movements Page
                                            </a>
                                        </template>
                                    </AdminBlock>
                                </div>
                                <div class="col-12">
                                    <PlotRoute
                                        :points="points"
                                        :polyline="movement.route_polyline"
                                        :remove-title="true"
                                        class="mb-4"
                                    ></PlotRoute>
                                </div>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="row">
                                <div class="col-12">
                                    <AdminBlock title="Collection">
                                        <p class="mb-2">
                                            <strong>
                                                Date:
                                            </strong>
                                            {{ formatDate(movement.collection_date) }} <TimeLabel :time="movement.collection_time" />
                                        </p>
                                        <p class="mb-0">
                                            <strong>
                                                Location:
                                            </strong>
                                            {{ movement.collection_company ? movement.collection_company + ', ' : '' }}{{ movement.collection_location }}
                                        </p>
                                    </AdminBlock>
                                </div>
                                <div class="col-12">
                                    <AdminBlock title="Delivery">
                                        <p class="mb-2">
                                            <strong>
                                                Date:
                                            </strong>
                                            {{ formatDate(movement.delivery_date) }} <TimeLabel :time="movement.delivery_time" />
                                        </p>
                                        <p class="mb-0">
                                            <strong>
                                                Location:
                                            </strong>
                                            {{ movement.delivery_company ? movement.delivery_company + ', ' : '' }}{{ movement.delivery_location }}
                                        </p>
                                    </AdminBlock>
                                </div>
                                <div class="col-12">
                                    <AdminBlock title="Job">
                                        <p class="mb-2">
                                            <strong>
                                                Customer:
                                            </strong>
                                            <template v-if="movement.job?.customer?.name">
                                                <a :href="customerLink" target="_blank">
                                                    {{ movement.job.customer.name }}
                                                </a>
                                            </template>
                                            <template v-else>
                                                N/A
                                            </template>
                                        </p>
                                        <p class="mb-2">
                                            <strong>
                                                Contract:
                                            </strong>
                                            <template v-if="movement?.job?.contract?.name && movement?.job?.contract?.name.length > 0">
                                                <a :href="contractLink" target="_blank">
                                                    {{ movement.job.contract.name }}
                                                </a>
                                            </template>
                                            <template v-else>
                                                N/A
                                            </template>
                                        </p>
                                        <p class="mb-2">
                                            <strong>
                                                Vehicle ID:
                                            </strong>
                                            <template v-if="movement.job?.vehicle?.reg">
                                                <a :href="vehicleLink" target="_blank">
                                                    {{ movement.job.vehicle.reg }}
                                                </a>
                                            </template>
                                            <template v-else>
                                                N/A
                                            </template>
                                        </p>
                                        <p class="mb-0">
                                            <strong>
                                                Vehicle Description:
                                            </strong>
                                            <template v-if="movement.job?.vehicle">
                                                <template v-if="movement.job.vehicle.description">
                                                    {{ movement.job.vehicle.description }}
                                                    <span v-if="movement.job?.vehicle?.vehicle_type?.type" style="white-space:nowrap">
                                                            ( {{ movement.job.vehicle.vehicle_type.type }} )
                                                        </span>
                                                </template>
                                            </template>
                                            <template v-else>
                                                N/A
                                            </template>
                                        </p>
                                    </AdminBlock>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-6">
                            <AdminBlock title="Proof of Collection">
                                <template v-if="movement.poc">
                                    <p class="mb-2"><strong>Action:</strong> {{ movement.poc?.action ?? 'No Action' }}</p>
                                    <p class="mb-2"><strong>Damage:</strong> {{ movement.poc?.damage ?? 'No Damage' }}</p>
                                    <p class="mb-2"><strong>Mileage:</strong> {{ movement.poc?.mileage ?? 'No Mileage' }}</p>
                                    <p class="mb-0"><strong>Fuel:</strong> {{ movement.poc?.fuel ?? 'No Fuel' }}</p>
                                </template>

                                <template v-else>
                                    <p class="mb-0"><em>Movement has not been started yet</em></p>
                                </template>
                            </AdminBlock>
                        </div>
                        <div class="col-6">
                            <AdminBlock title="Proof of Delivery">
                                <template v-if="movement.pod">
                                    <p class="mb-2"><strong>Action:</strong> {{ movement.pod?.action ?? 'No Action' }}</p>
                                    <p class="mb-2"><strong>Damage:</strong> {{ movement.pod?.damage ?? 'No Damage' }}</p>
                                    <p class="mb-2"><strong>Mileage:</strong> {{ movement.pod?.mileage ?? 'No Mileage' }}</p>
                                    <p class="mb-0"><strong>Fuel:</strong> {{ movement.pod?.fuel ?? 'No Fuel' }}</p>
                                </template>

                                <template v-else>
                                    <p class="mb-0"><em>Movement has not been finished yet</em></p>
                                </template>
                            </AdminBlock>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div class="d-flex justify-content-center">
                                <a :href="reportLink" class="btn btn-sm btn-secondary">
                                    Download Report
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-sm btn-danger" data-bs-dismiss="modal">
                        Close
                    </button>

                    <a class="btn btn-sm btn-success" :href="jobLink">View Job</a>

                    <a class="btn btn-sm btn-primary" :href="movementLink">View Movement</a>

                    <template v-if="showMoveToToday">
                        <button type="button" class="btn btn-sm btn-success" @click="moveToToday">
                            Move to Today
                        </button>
                    </template>
                </div>
            </div>
        </div>
    </div>

</template>

<style scoped>
    .tooltip-inner {
        max-width: 500px !important;
        padding: .75rem 1rem !important;
    }

    .floating-movement {
        position: relative;
        cursor: grab;
        transition: all 0.2s ease;
        user-select: none;
        overflow: visible;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        list-style: none;
    }

    .floating-movement:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        transform: translateY(-1px);
    }

    .floating-movement:active {
        cursor: grabbing;
    }

    /* Drag handle styling */
    .drag-handle {
        width: 44px;
        min-width: 44px;
        background: rgba(255, 255, 255, 0.15);
        color: rgba(255, 255, 255, 0.7);
        font-size: 18px;
        cursor: grab;
        transition: all 0.2s ease;
        border-right: 1px solid rgba(255, 255, 255, 0.1);
    }

    .drag-handle:hover {
        background: rgba(255, 255, 255, 0.25);
        color: white;
    }

    .floating-movement:active .drag-handle {
        cursor: grabbing;
        background: rgba(255, 255, 255, 0.3);
    }

    /* When this item is being dragged */
    .floating-movement.dragging {
        opacity: 0.3;
        transform: scale(0.95);
        box-shadow: none;
    }

    /* When dragging over this item - show drop indicator */
    .floating-movement.drag-over-target {
        transform: translateY(4px);
        box-shadow: 0 -4px 0 0 #4caf50, 0 2px 8px rgba(0, 0, 0, 0.2);
    }

    .floating-movement.drag-over-target::before {
        content: '';
        position: absolute;
        top: -8px;
        left: 0;
        right: 0;
        height: 4px;
        background: #4caf50;
        border-radius: 2px;
    }

    .floating-movement[data-status="aborted"] .modal-icon {
        background:red;
    }

    .floating-movement[data-status="assigned"] .modal-icon {
        background:orange;
    }

    .floating-movement[data-status="finished"] .modal-icon,
    .floating-movement[data-status="finished-manually"] .modal-icon {
        background: blue;
    }

    .floating-movement[data-status="started"] .modal-icon {
        background:green;
    }

    /* Highlight Flash effect */
    .highlight-flash {
        background-color: #d6960e !important;
        box-shadow: 0 0 15px rgb(103 79 0 / 0.8) !important;
        z-index: 10;
    }

    .highlight-flash .movement-content,
    .highlight-flash .movement-content *,
    .highlight-flash .drag-handle i {
        color: #ffffff !important;
    }

    .highlight-flash .drag-handle {
        background: rgba(146, 64, 14, 0.1) !important;
        border-right: 1px solid rgba(146, 64, 14, 0.1) !important;
    }
</style>

<script setup>

    import { ref, computed, onMounted, nextTick, watch, inject } from 'vue';
    import { errorAlert } from '@/utils';
    import { useMovementFormatting } from '@/composables/useMovementFormatting';
    import { useOptionalDragDropContext } from '@/composables/useDragAndDrop';
    import axios from 'axios';
    import Tooltip from 'bootstrap/js/dist/tooltip';
    import Modal from 'bootstrap/js/dist/modal';
    import TimeLabel from '@/components/movements/components/TimeLabel.vue';
    import Transported from '@/components/movements/jobs/planner/Transported.vue';
    import AdminBlock from '@/components/movements/components/AdminBlock.vue';
    import PlotRoute from '@/components/movements/components/PlotRoute.vue';
    import { DateTime } from 'luxon';
    import {successAlert} from "@//utils";

    const { formatLocale, formatDate, formatRelativeTime } = useMovementFormatting();
    const markLocalMovementMutation = inject('markLocalMovementMutation', () => {});
    const movement = defineModel();

    const props = defineProps({
        licences: {
            type: String,
            default: null
        },
        lastItem: {
            type: Boolean,
            default: false
        },
        classes: {
            type: Array,
            default: () => []
        },
        enableMovementModal: {
            type: Boolean,
            default: true
        },
        index: {
            type: Number,
            required: true
        },
        dropzoneId: {
            type: String,
            required: true
        },
        isDragOverIndex: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits(['dragover-index', 'drop-at-index', 'movement-moved-off-date']);

    // Drag and drop context
    const dragDropContext = useOptionalDragDropContext();
    const isDragDropEnabled = computed(() => !!dragDropContext);
    const isDragging = ref(false);

    const isRecentlyUpdated = ref(false);
    let flashTimeout = null;

    watch(() => movement.value?._websocket_updated_at, (newVal) => {
        if (newVal && (Date.now() - newVal) < 2000) {
            isRecentlyUpdated.value = true;
            if (flashTimeout) clearTimeout(flashTimeout);
            flashTimeout = setTimeout(() => {
                isRecentlyUpdated.value = false;
            }, 2000);
        }
    }, { immediate: true });

    const tooltipInstance = ref(null);
    const tooltipText = ref(movement.value?.created_at ?? '');
    const modalInstance = ref(null);
    const modal = ref(null);
    const tooltipElement = ref(null);
    const modalBody = ref(null);

    const points = computed(() => [
        {
            type: 'collection',
            lat: movement.value?.collection_lat ?? '',
            lng: movement.value?.collection_lng ?? '',
            infoWindowHeaderContent: 'Collection',
            infoWindowContent: movement.value?.collection_location ?? '',
            markerColour: 'green',
            title: movement.value?.collection_location ?? '',
        },
        {
            type: 'delivery',
            lat: movement.value?.delivery_lat ?? '',
            lng: movement.value?.delivery_lng ?? '',
            infoWindowHeaderContent: 'Delivery',
            infoWindowContent: movement.value?.delivery_location ?? '',
            markerColour: 'red',
            title: movement.value?.delivery_location ?? '',
        },
    ]);

    const compModalIdentifier = computed(() => `movement_modal_${movement.value.id}`);

    const customerLink = computed(() => movement.value.job && movement.value.job.customer
        ? route('movements.customers.show', movement.value.job.customer)
        : '#');

    const contractLink = computed(() => movement.value.job && movement.value.job.contract
        ? route('movements.contracts.show', movement.value.job.contract)
        : '#');

    const vehicleLink = computed(() => movement.value.job && movement.value.job.vehicle
        ? route('movements.vehicles.show', movement.value.job.vehicle)
        : '#');

    const jobLink = computed(() => movement.value.job
        ? route('movements.jobs.show', movement.value.job.id)
        : '#');

    const movementLink = computed(() => movement.value.id
        ? route('movements.movements.show', movement.value.id)
        : '#');

    const reportLink = computed(() => movement.value.id
        ? route('movements.evidence.report', movement.value.id)
        : '#');

    const showMoveToToday = computed(() => {
        if (!movement.value.collection_date) return false;
        const collectionDate = DateTime.fromISO(movement.value.collection_date);
        return collectionDate < DateTime.now().startOf('day');
    });

    const moveToToday = () => {
        markLocalMovementMutation([movement.value.id]);

        axios.post(route('api.movements.jobs.planner.move-to-today'), { movements: [movement.value.id] })
            .then(( response ) => {
                successAlert(response.data.title, 'Success');
                if (modalInstance.value) {
                    modalInstance.value.hide();
                }

                emit('movement-moved-off-date', movement.value.id);
            }).catch(( error ) => {
            console.log(error);
            errorAlert(error, 'Could not move movement to today');
        });
    }

    /*
     * This exists because I think the bootstrap tooltips were getting weird how they were being initialized/destroyed
     * on elements that may or may not exist in the DOM at certain times (drag and drop)
     */
    function disposeTooltip() {
        if (tooltipInstance.value) {
            try {
                tooltipInstance.value.dispose();
            } catch (e) {
                // Tooltip may already be disposed or element destroyed
            }
            tooltipInstance.value = null;
        }
    }

    function initTooltip() {
        tooltipText.value = formatRelativeTime(movement.value?.created_at ?? '');
        nextTick(() => {
            disposeTooltip();
            // Only initialize if element exists and is in the DOM
            if (tooltipElement.value && document.body.contains(tooltipElement.value)) {
                tooltipInstance.value = new Tooltip(tooltipElement.value);
            }
        });
    }

    function showModal() {
        if (!props.enableMovementModal) return;
        modalInstance.value.show();
        disposeTooltip();
    }


    function driverEditLink() {
        return route('movements.movements.show', { movement: movement.value, edit_driver_text: true });
    }

    // Drag and drop handlers
    function onDragStart(event) {
        if (!dragDropContext) return;

        isDragging.value = true;

        // Set drag data
        const dragRect = event.currentTarget?.getBoundingClientRect?.();
        const payload = JSON.stringify({
            itemId: movement.value?.id,
            index: props.index,
            dropzoneId: props.dropzoneId,
            dragOffsetX: dragRect ? event.clientX - dragRect.left : null,
            dragOffsetY: dragRect ? event.clientY - dragRect.top : null,
            dragSnapshotWidth: dragRect?.width ?? event.currentTarget?.offsetWidth ?? null,
            dragSnapshotHtml: event.currentTarget?.outerHTML ?? null,
        });

        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', payload);
        event.dataTransfer.setData('text', payload);

        // Hide default drag image
        const emptyImg = document.createElement('img');
        emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        event.dataTransfer.setDragImage(emptyImg, 0, 0);

        // Start drag in context
        dragDropContext.startDrag(movement.value, props.index, props.dropzoneId, event);

        // Dispose tooltip while dragging
        if (tooltipInstance.value) {
            tooltipInstance.value.dispose();
            tooltipInstance.value = null;
        }
    }

    function onDrag(event) {
        if (!dragDropContext) return;
        dragDropContext.updateGhostPosition(event);
    }

    function onDragEnd() {
        if (!dragDropContext) return;

        isDragging.value = false;
        dragDropContext.endDrag();

        // Reinitialize tooltip after drag
        nextTick(() => {
            initTooltip();
        });
    }

    function onDragOver(event) {
        if (!dragDropContext) return;

        // Update ghost position when dragging over this item
        dragDropContext.updateGhostPosition(event);
        emit('dragover-index', props.index);
    }

    function onDragEnter() {
        if (!dragDropContext) return;
        emit('dragover-index', props.index);
    }

    function onDropOnItem(event) {
        if (!dragDropContext) return;
        emit('drop-at-index', event, props.index);
    }

    onMounted(() => {
        initTooltip();
        modalInstance.value = new Modal(modal.value);
    });
</script>
